import { Check, Trash2, X } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { fmtDate, fmtDuration, fmtEur } from "../../lib/api";
import { InvoiceButton, IvStatus } from "../pro/ExpertMissions";
import { ExampleBadge } from "../site/Primitives";
import { VerifiedBadge, expertVerifiedSince } from "../site/VerifiedBadge";

const STATUSES = ["new", "in_progress", "resolved", "closed"];

const Empty = () => { const { t } = useLang(); return <p className="empty">{t.admin.empty}</p>; };

const ExpertSelect = ({ experts, value, onChange, label, testid }) => {
  const { t } = useLang();
  return (
    <select className="f-input status-select" value={value || ""} onChange={(e) => e.target.value && onChange(e.target.value)} aria-label={label} data-testid={testid}>
      <option value="">{t.adminX.assignPh}</option>
      {experts.map((e) => <option key={e.id} value={e.id}>{`${e.name} — ${e.verticals.map((v) => t.verticalsShort[v]).join("/")} · ${e.hourly_rate} €/h`}</option>)}
    </select>
  );
};

export const RequestsTable = ({ rows, experts, onStatus, onAssign }) => {
  const { t, lang } = useLang();
  const h = t.admin.h;
  if (!rows.length) return <Empty />;
  return (
    <div className="table-wrap"><table className="data-table" data-testid="admin-requests-table" style={{ minWidth: 980 }}>
      <thead><tr><th>{h.ref}</th><th>{h.date}</th><th>{h.company}</th><th>{h.contact}</th><th>{h.vertical}</th><th>{h.need}</th><th>{h.urgency}</th><th>{h.email}</th><th>{t.adminX.assign}</th><th>{h.status}</th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.id} data-testid={`admin-request-row-${r.ref}`}>
          <td className="mono strong">{r.ref}</td>
          <td className="mono">{fmtDate(r.created_at, lang)}</td>
          <td><span className="strong">{r.company}</span><br /><small style={{ color: "var(--text-2)" }}>{r.country}</small></td>
          <td>{r.contact}<br /><small style={{ color: "var(--text-2)" }}>{r.email}{r.phone ? ` · ${r.phone}` : ""}</small></td>
          <td>{t.verticalsShort[r.vertical]}</td>
          <td title={r.description}>{t.needs[r.need]}<br /><small style={{ color: "var(--text-2)" }}>{r.description.slice(0, 80)}{r.description.length > 80 ? "…" : ""}</small></td>
          <td>{t.urgencies[r.urgency]}</td>
          <td className="mono" data-testid={`admin-email-status-${r.ref}`}>
            admin : {t.adminX.emailStatus[r.email_status] || r.email_status}<br />
            <small style={{ color: "var(--text-2)" }}>{t.adminX.confirmation} : {t.adminX.emailStatus[r.confirmation_status] || r.confirmation_status || "—"}</small>
          </td>
          <td>
            <div className="assign-cell">
              <ExpertSelect experts={experts} value={r.assigned_expert_id} onChange={(id) => onAssign(r.id, id)} label={`${t.adminX.assign} ${r.ref}`} testid={`admin-assign-select-${r.ref}`} />
              {r.preferred_expert_name && <small>{r.preferred_expert_name} ({t.adminX.preferred})</small>}
            </div>
          </td>
          <td>
            <select className="f-input status-select" value={r.status} onChange={(e) => onStatus(r.id, e.target.value)} aria-label={`${h.status} ${r.ref}`} data-testid={`admin-status-select-${r.ref}`}>
              {STATUSES.map((s) => <option key={s} value={s}>{t.statuses[s]}</option>)}
            </select>
          </td>
        </tr>
      ))}</tbody>
    </table></div>
  );
};

const EXPERT_C = { pending: "var(--amber)", approved: "#6FCF97", rejected: "var(--rust-text)" };
const FILTERS = ["all", "pending", "approved", "rejected"];

export const ExpertStatusPill = ({ status, testid }) => {
  const { t } = useLang();
  return <span className="pill" style={{ "--c": EXPERT_C[status], "--ct": EXPERT_C[status] }} data-testid={testid}>{t.approval.status[status]}</span>;
};

const ExpertActions = ({ e, onReview, onDelete }) => {
  const { t } = useLang();
  const ap = t.approval;
  return (
    <div className="row-actions">
      {e.status !== "approved" && <button type="button" className="btn btn-ghost btn-sm btn-approve" onClick={() => onReview(e, "approved")} data-testid={`admin-expert-approve-btn-${e.id}`}><Check aria-hidden="true" />{ap.approve}</button>}
      {e.status !== "rejected" && <button type="button" className="btn btn-ghost btn-sm" onClick={() => onReview(e, "rejected")} data-testid={`admin-expert-reject-btn-${e.id}`}><X aria-hidden="true" />{ap.reject}</button>}
      <button type="button" className="btn btn-danger btn-sm" onClick={() => onDelete(e)} data-testid={`admin-expert-delete-btn-${e.id}`}><Trash2 aria-hidden="true" />{t.admin.del}</button>
    </div>
  );
};

export const ExpertFilter = ({ rows, value, onChange }) => {
  const { t } = useLang();
  const count = (k) => (k === "all" ? rows.length : rows.filter((e) => e.status === k).length);
  return (
    <div className="filter-group" role="group" aria-label={t.approval.filterLabel} data-testid="admin-expert-filter">
      {FILTERS.map((k) => (
        <button key={k} type="button" className="btn btn-ghost btn-sm" aria-pressed={value === k} onClick={() => onChange(k)} data-testid={`admin-expert-filter-${k}`}>
          {k === "all" ? t.approval.all : t.approval.status[k]} ({count(k)})
        </button>
      ))}
    </div>
  );
};

export const ExpertsTable = ({ rows, onToggle, onDelete, onReview }) => {
  const { t, lang } = useLang();
  const h = t.admin.h;
  if (!rows.length) return <Empty />;
  return (
    <div className="table-wrap"><table className="data-table" data-testid="admin-experts-table" style={{ minWidth: 1040 }}>
      <thead><tr><th>{h.name}</th><th>{t.approval.h}</th><th>{h.country}</th><th>{h.vertical}</th><th>{h.skills}</th><th>{h.rate}</th><th>{h.availability}</th><th>{h.example}</th><th>{h.actions}</th></tr></thead>
      <tbody>{rows.map((e) => (
        <tr key={e.id} data-testid={`admin-expert-row-${e.id}`}>
          <td><span className="strong">{e.name}</span> {e.is_example && <ExampleBadge />}<VerifiedBadge compact since={expertVerifiedSince(e)} testid={`admin-expert-verified-${e.id}`} /><br /><small style={{ color: "var(--text-2)" }}>{e.email}</small></td>
          <td>
            <ExpertStatusPill status={e.status} testid={`admin-expert-status-${e.id}`} />
            {e.rejection_reason && <><br /><small style={{ color: "var(--text-2)" }} title={e.rejection_reason}>{e.rejection_reason.slice(0, 60)}</small></>}
            {e.pending_alert_status && <><br /><small style={{ color: "var(--text-2)" }} data-testid={`admin-expert-alert-status-${e.id}`}>{t.alerts.statusLabel} {t.alerts.status[e.pending_alert_status] || e.pending_alert_status}{e.pending_alert_at ? ` · ${fmtDate(e.pending_alert_at, lang)}` : ""}</small></>}
          </td>
          <td>{e.country}</td>
          <td>{e.verticals.map((v) => t.verticalsShort[v]).join(", ")}</td>
          <td style={{ maxWidth: 220 }}>{e.skills.join(", ")}</td>
          <td className="mono">{e.hourly_rate} {t.common.perHour}</td>
          <td>{t.availability[e.availability]}</td>
          <td>
            <button type="button" role="switch" className="toggle" aria-checked={e.is_example} aria-label={`${h.example} — ${e.name}`}
              onClick={() => onToggle(e.id, !e.is_example)} data-testid={`admin-expert-example-toggle-${e.id}`} />
          </td>
          <td><ExpertActions e={e} onReview={onReview} onDelete={onDelete} /></td>
        </tr>
      ))}</tbody>
    </table></div>
  );
};

export const InterventionsAdminTable = ({ rows, experts, onReassign }) => {
  const { t, lang } = useLang();
  const h = t.admin.h;
  if (!rows.length) return <Empty />;
  return (
    <div className="table-wrap"><table className="data-table" data-testid="admin-interventions-table" style={{ minWidth: 960 }}>
      <thead><tr><th>{h.ref}</th><th>{h.date}</th><th>{h.expert}</th><th>{h.client}</th><th>{h.duration}</th><th>{h.amount}</th><th>{h.commission}</th><th>{h.status}</th><th>{h.pdf}</th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.id} data-testid={`admin-intervention-row-${r.ref}`}>
          <td className="mono strong">{r.ref}</td>
          <td className="mono">{fmtDate(r.assigned_at, lang)}</td>
          <td>
            {r.status === "assigned"
              ? <ExpertSelect experts={experts} value={r.expert_id} onChange={(id) => onReassign(r.id, id)} label={`${t.adminX.reassign} ${r.ref}`} testid={`admin-reassign-select-${r.ref}`} />
              : r.expert_name}
            <br /><small style={{ color: "var(--text-2)" }}>{r.hourly_rate} {t.common.perHour}</small>
          </td>
          <td>{r.client_company}<br /><small style={{ color: "var(--text-2)" }}>{r.client_email}{r.site ? ` · ${r.site}` : ""}</small></td>
          <td className="mono">{fmtDuration(r.duration_seconds)}</td>
          <td className="mono">{fmtEur(r.amount_ht, lang)}</td>
          <td className="mono">{fmtEur(r.commission, lang)} ({r.commission_pct}%)</td>
          <td><IvStatus status={r.status} /></td>
          <td>{r.status === "completed" && <InvoiceButton iv={r} />}</td>
        </tr>
      ))}</tbody>
    </table></div>
  );
};

const WA_C = { sent: "#6FCF97", failed: "var(--rust-text)", skipped: "var(--text-2)" };

export const WhatsAppTable = ({ rows }) => {
  const { t, lang } = useLang();
  const x = t.adminX;
  if (!rows.length) return <Empty />;
  return (
    <div className="table-wrap"><table className="data-table" data-testid="admin-whatsapp-table" style={{ minWidth: 900 }}>
      <thead><tr>{["date", "event", "ref", "role", "number", "status", "detail"].map((k) => <th key={k}>{x.waH[k]}</th>)}</tr></thead>
      <tbody>{rows.map((l) => (
        <tr key={l.id}>
          <td className="mono">{fmtDate(l.created_at, lang)} {new Date(l.created_at).toLocaleTimeString(lang === "en" ? "en-GB" : "fr-FR", { hour: "2-digit", minute: "2-digit" })}</td>
          <td>{x.events[l.event] || l.event}</td>
          <td className="mono strong">{l.ref || "—"}</td>
          <td>{x.roles[l.recipient_role] || l.recipient_role}</td>
          <td className="mono">{l.number || "—"}</td>
          <td><span className="pill" style={{ "--c": WA_C[l.status], "--ct": WA_C[l.status] }}>{x.waStatus[l.status] || l.status}</span></td>
          <td style={{ maxWidth: 320, fontSize: 12 }}>{l.detail || (l.message_id ? `id ${l.message_id}` : "—")}</td>
        </tr>
      ))}</tbody>
    </table></div>
  );
};
